import { useEffect, useState } from 'react'

import { API } from '@/api'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { EmptyView, Trans } from '@/components'
import { BILL_CATALOG } from '@/consts/billing'
import { MachineBill } from '@/models/bill'
import { Instance } from '@/models/instance'
import { InstanceConfig } from '@/pages/instance-list/components/InstanceConfig'
import { formatDatePreset, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { TeamInstanceAccess } from './TeamInstanceAccess'
import { TeamInstanceHardware } from './TeamInstanceHardware'

interface InstanceDetailContentProps {
  id: string
  name: string
  region: string
  status: {
    label: string
    style: string
  }
  cost: string
  ownerName: string
  instance: Instance
  teamResID: string
  memberResID: string
  onCopyId: (id: string) => void
}

export function InstanceDetailContent({
  id,
  name,
  region,
  status,
  cost,
  ownerName,
  instance,
  teamResID,
  memberResID,
  onCopyId,
}: InstanceDetailContentProps) {
  const [billData, setBillData] = useState<MachineBill | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBillingData = async () => {
      if (!teamResID || !memberResID) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await API.getTeamMemberBills({
          page: 1,
          per_page: 999,
          res_id: teamResID,
          member_res_ids: [memberResID],
          catalog: BILL_CATALOG.INSTANCES,
        })

        if (response.code === 0 && response.bills && response.bills.length > 0) {
          // Filter bills for this specific instance
          const instanceBills = response.bills
            .filter((item) => item.bill.resID === instance.resID)
            .sort((a, b) => b.bill.billTime.seconds - a.bill.billTime.seconds)

          if (instanceBills.length > 0) {
            // Get the latest bill
            const latestBill = new MachineBill(instanceBills[0].bill)
            setBillData(latestBill)
          }
        }
      } catch (error) {
        console.error('Failed to fetch billing data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchBillingData()
  }, [teamResID, memberResID, instance.resID])

  return (
    <>
      {/* Section: Basic Information */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">
            <Trans i18nKey="team.instance.detail.basicInfo">Basic Information</Trans>
          </p>
        </div>

        {/* Data Card */}
        <div className="bg-panel-gray-100 p-4 rounded-lg flex flex-col gap-4">
          {/* Row 1: ID, Name, Region */}
          <div className="grid grid-cols-3 gap-4">
            {/* ID with Copy */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="instance.tableHeaderID">ID</Trans>
              </p>
              <div className="flex gap-1 items-center">
                <p className="t-sb text-primary">{id}</p>
                <button onClick={() => onCopyId(id)} className="cursor-pointer">
                  <CopyIcon className="w-4 h-4 text-highlight" />
                </button>
              </div>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="instance.tableHeaderName">Name</Trans>
              </p>
              <p className="t-sb text-primary">{name}</p>
            </div>

            {/* Region */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="instance.tableHeaderRegion">Region</Trans>
              </p>
              <p className="t-sb text-primary">{region}</p>
            </div>
          </div>

          {/* Row 2: Status, Cost, Empty */}
          <div className="grid grid-cols-3 gap-4">
            {/* Status */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="instance.tableHeaderStatus">Status</Trans>
              </p>
              <p className={cn('t-sb', status.style)}>{status.label}</p>
            </div>

            {/* Cost */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="instance.tableHeaderCost">Cost</Trans>
              </p>
              <p className="t-sb text-primary">{cost}</p>
            </div>

            {/* Empty */}
            <div></div>
          </div>
        </div>
      </div>

      {/* Section: Owner Information */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">
            <Trans i18nKey="team.admin.table.owner">Owner</Trans>
          </p>
        </div>

        {/* Owner Card */}
        <div className="bg-panel-gray-100 grid grid-cols-2 gap-4 p-4 rounded-lg">
          <div className="flex flex-col gap-1">
            <p className="t-xs text-secondary">
              <Trans i18nKey="team.admin.table.originOwner">Origin Owner</Trans>
            </p>
            <p className="t-sb text-primary">{ownerName}</p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="t-xs text-secondary">
              <Trans i18nKey="team.admin.table.owner">Owner</Trans>
            </p>
            <p className="t-sb text-primary">{ownerName}</p>
          </div>
        </div>
      </div>

      {/* Section: Access */}
      {instance.isRunning && <TeamInstanceAccess instance={instance} />}

      {/* Section: Billing */}
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">Billing</p>
        </div>

        {loading ? (
          <div className="bg-panel-gray-100 grid grid-cols-5 gap-4 p-4 rounded-lg">
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Start Time</div>
              <div className="t-sb text-primary">Loading...</div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">End Time</div>
              <div className="t-sb text-primary">Loading...</div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Price</div>
              <div className="t-sb text-primary">Loading...</div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Cost</div>
              <div className="t-sb text-primary">Loading...</div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Duration</div>
              <div className="t-sb text-primary">Loading...</div>
            </div>
          </div>
        ) : billData ? (
          <div className="bg-panel-gray-100 grid grid-cols-5 gap-4 p-4 rounded-lg">
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Start Time</div>
              <div className="t-sb text-primary">
                {formatDatePreset(billData.startTime, 'minute')}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">End Time</div>
              <div className="t-sb text-primary">
                {billData.isRunning ? 'Running' : formatDatePreset(billData.endTime, 'minute')}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Price</div>
              <div className="t-sb text-primary">{billData.priceStr}</div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Cost</div>
              <div className="t-sb text-primary">
                {formatPrice(Math.abs(billData.amount), false)}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="t-xs text-secondary">Duration</div>
              <div className="t-sb text-primary">{billData.usedTimeStr}</div>
            </div>
          </div>
        ) : (
          <div className="bg-panel-gray-100 p-4 rounded-lg">
            <EmptyView title="No billing data" imageSize="small" />
          </div>
        )}
      </div>

      {/* Section: Config */}
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">Config</p>
        </div>

        <InstanceConfig instance={instance} />
      </div>

      {/* Section: Hardware */}
      {(instance.isRunning || instance.hardware) && <TeamInstanceHardware instance={instance} />}
    </>
  )
}
